import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  useTheme,
  Box,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAppSelector, useAppDispatch } from '@/app/store';
import { setImagePreviewModal } from '../uiSlice';

/**
 * Redux-connected ImagePreviewModal.
 * This component reads the preview state from Redux and renders the modal.
 * Place this component high in the component tree (e.g., AppLayout).
 *
 * To open the modal from any component, dispatch:
 *   dispatch(setImagePreviewModal(imageUrl))
 *
 * To close:
 *   dispatch(setImagePreviewModal(null))
 */
const ImagePreviewModal = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { open, imageUrl } = useAppSelector(
    state => state.ui.imagePreviewModal
  );

  const handleClose = () => {
    dispatch(setImagePreviewModal(null));
  };

  const isVideo = imageUrl?.endsWith('.mp4');

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Media Preview
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
        }}
      >
        {imageUrl &&
          (isVideo ? (
            <video
              src={imageUrl}
              controls
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                borderRadius: 8,
              }}
            />
          ) : imageUrl.startsWith('http') &&
            !imageUrl.match(/\.(jpeg|jpg|gif|png|webp|svg|mp4)$/i) ? (
            <Box
              sx={{
                p: 3,
                textAlign: 'center',
                width: '100%',
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Web Source URL
              </Typography>
              <Typography
                component="a"
                href={imageUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  wordBreak: 'break-all',
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {imageUrl}
              </Typography>
            </Box>
          ) : (
            <img
              src={imageUrl}
              alt="Preview"
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
                borderRadius: 8,
              }}
            />
          ))}
      </DialogContent>
    </Dialog>
  );
};

export default ImagePreviewModal;
